import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { Form, Formik } from 'formik'
import {
  Button,
  Dialog,
  makeStyles,
  Typography,
  CircularProgress,
} from '@material-ui/core'

import useTransfer from 'commons/useTransfer'
import MessageAlert from 'app/components/MessageAlert'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDialog-paper': {
      width: '375px',
      overflowX: 'auto',
      padding: '3rem',
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  button: {
    '& .button-close': {
      textTransform: 'none',
      padding: '0.75rem 1.25rem',
      color: theme.colors['grey-200'],
      border: '1px solid',
      borderColor: theme.colors['grey-200'],
      borderRadius: '4px',
      backgroundColor: 'transparent !important',
      '&:hover': {
        color: theme.colors['grey-400'],
        borderColor: theme.colors['grey-400'],
      },
      '&:action': {
        color: theme.colors['grey-300'],
        borderColor: theme.colors['grey-300'],
      },
    },
    '& .button-submit': {
      backgroundColor: theme.colors['primary'],
      color: theme.palette.light.main,
      textTransform: 'none',
      padding: '0.75rem 1.25rem',
      '&:hover': {
        backgroundColor: theme.colors['primary-500'],
      },
      '&:action': {
        backgroundColor: theme.colors['primary'],
      },
    },
    '& .MuiButton-contained.Mui-disabled': {
      backgroundColor: `${theme.colors['grey-100']} !important`,
    },
  },
}))

const Transfer = (props) => {
  const { id, name, onSuccess } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { createButton } = useTransfer()
  const [createLoading, create] = createButton()

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(createLoading)

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleSubmit = async (id, password) => {
    try {
      await create(id, password)
      onSuccess(true)
      setIsOpen(false)
    } catch (error) {
      MessageAlert.showNetworkError(formatMessage)(error)
      onSuccess(false)
    }
  }

  useEffect(() => {
    if (createLoading) setIsLoading(createLoading)
    else setIsLoading(false)
  }, [createLoading])

  return (
    <>
      <Button
        className="text-14 text-primary normal-case p-0 hover:bg-transparent"
        onClick={handleOpen}
      >
        {formatMessage({
          id: 'teamMembers.transfer.label',
        })}
      </Button>
      <Dialog open={isOpen} onClose={handleClose} className={classes.root}>
        <div className="mt-3 text-center text-primary">
          <Typography className="text-lg font-bold leading-6">
            {formatMessage({
              id: 'transfer.dialog.title',
            })}
          </Typography>
          <div className="my-5 space-y-3 text-left">
            <Typography className="text-14 text-primary">
              {formatMessage({
                id: 'transfer.dialog.content.start',
              })}
              <b className="mx-2">{name}</b>
            </Typography>
            <ul className="text-left list-disc list-inside space-y-3 text-14 text-primary pl-5 ">
              <li>
                {formatMessage({
                  id: 'transfer.dialog.list1',
                })}
              </li>
              <li>
                {formatMessage({
                  id: 'transfer.dialog.list2',
                })}
              </li>
              <li>
                {formatMessage({
                  id: 'transfer.dialog.list3',
                })}
              </li>
            </ul>
            <Formik
              initialValues={{
                password: '',
              }}
              validationSchema={Yup.object({
                password: Yup.string()
                  .min(
                    6,
                    formatMessage({
                      id: 'resetPassword.textField.error.validation.minLength',
                    }),
                  )
                  .required(
                    formatMessage({
                      id: 'error.form.required',
                    }),
                  ),
              })}
              onSubmit={(values) => {
                handleSubmit(id, values.password)
              }}
            >
              {({ values, errors, touched, handleBlur, handleChange }) => (
                <Form>
                  <div className="border-t-1 border-b-1 border-neutrals-200 py-5">
                    <LabelInput
                      label={formatMessage({
                        id: 'transfer.password',
                      })}
                      required
                    >
                      <SimpleInput
                        className={classes.input}
                        id="transfer-dialog-input-password"
                        name="password"
                        type="password"
                        placeholder={formatMessage({
                          id: 'login.textField.placeHolder.password',
                        })}
                        value={values.password}
                        size="medium"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                      />
                    </LabelInput>
                  </div>

                  <div
                    className={clsx(
                      classes.button,
                      'flex justify-center space-x-5 mt-10',
                    )}
                  >
                    <Button className="button-close" onClick={handleClose}>
                      {formatMessage({
                        id: 'action.cancel',
                      })}
                    </Button>

                    <Button
                      size="small"
                      type="submit"
                      variant="contained"
                      className="button-submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress
                          size={24}
                          style={{ color: 'white' }}
                        />
                      ) : (
                        formatMessage({
                          id: 'transfer.dialog.btn',
                        })
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Dialog>
    </>
  )
}

Transfer.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  onSuccess: PropTypes.func,
}

export default Transfer
